import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { Box, Button, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { courseIcons } from '../components/courseIcons';
import ProgressRing from '../../student/components/ProgressRing';
import {
    completedLessonCount, coursePercent, getCurrentAssignment, lessonPercent, StudentCourse, StudentLesson,
} from '../../student/mockData';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SUCCESS_TEXT, SURFACE_SOFT, yellowButtonSx } from '../../student/studentStyles';

function LessonCard({ lesson, isCurrent, onOpen }: { lesson: StudentLesson; isCurrent: boolean; onOpen: (fromBeginning?: boolean) => void }) {
    const percent = lessonPercent(lesson);
    const complete = lesson.completedPrompts >= lesson.totalPrompts;
    const notStarted = lesson.completedPrompts === 0;
    const actionLabel = complete ? 'Practice again' : notStarted ? 'Start' : 'Continue';

    return (
        <Box sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, border: isCurrent ? `2px solid ${BLUE}` : '2px solid transparent' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2}>
                <ProgressRing percent={percent} size={52} thickness={5} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15.5, color: notStarted ? SLATE : NAVY }}>{lesson.name}</Typography>
                        {isCurrent && <Chip label="Current lesson" size="small" sx={{ height: 23, bgcolor: `${BLUE}0D`, color: BLUE, fontFamily: INTER, fontWeight: 700, fontSize: 11 }} />}
                        {complete && <Chip icon={<CheckCircleRoundedIcon />} label="Complete" size="small" sx={{ height: 24, bgcolor: `${GREEN}12`, color: SUCCESS_TEXT, fontFamily: INTER, fontWeight: 700, fontSize: 11, '& .MuiChip-icon': { color: GREEN, fontSize: 15 } }} />}
                    </Stack>
                    <Typography sx={{ mt: 0.5, fontFamily: INTER, fontSize: 12.5, color: complete ? SUCCESS_TEXT : SLATE }}>
                        {complete ? `${lesson.totalPrompts} exercises completed` : notStarted ? `${lesson.totalPrompts} exercises · Not started` : `${lesson.completedPrompts} of ${lesson.totalPrompts} exercises completed`}
                    </Typography>
                    {!complete && !notStarted && <LinearProgress variant="determinate" value={percent} sx={{ mt: 1.25, maxWidth: 360, height: 6, borderRadius: 8, bgcolor: RING_TRACK, '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: BLUE } }} />}
                </Box>
                <Stack spacing={0.5}>
                <Button
                    onClick={() => onOpen(complete)}
                    variant={isCurrent ? 'contained' : 'outlined'}
                    startIcon={complete ? <ReplayRoundedIcon /> : <PlayArrowRoundedIcon />}
                    sx={isCurrent ? { ...yellowButtonSx, minWidth: 118, fontSize: 13.5 } : { minWidth: 108, borderColor: RING_TRACK, color: NAVY, fontFamily: INTER, fontWeight: 700, fontSize: 13, textTransform: 'none', '&:hover': { borderColor: BLUE, bgcolor: `${BLUE}08` } }}
                >
                    {actionLabel}
                </Button>
                {!complete && !notStarted && <Button onClick={() => onOpen(true)} sx={{ minHeight: 44, color: BLUE, fontFamily: INTER, fontWeight: 600, fontSize: 13, textTransform: 'none' }}>Start from beginning</Button>}
                </Stack>
            </Stack>
        </Box>
    );
}

interface StudentDesktopLessonsPageProps {
    course: StudentCourse;
    onBack: () => void;
    onOpenLesson: (lesson: StudentLesson, fromBeginning?: boolean) => void;
}

export default function StudentDesktopLessonsPage({ course, onBack, onOpenLesson }: StudentDesktopLessonsPageProps) {
    const CourseIcon = courseIcons[course.icon];
    const percent = coursePercent(course);
    const completed = completedLessonCount(course);
    const current = getCurrentAssignment();
    const nextLesson = course.lessons.find(lesson => lesson.completedPrompts < lesson.totalPrompts);
    const currentLessonId = current?.course.id === course.id ? current.lesson.id : nextLesson?.id;
    const completedExercises = course.lessons.reduce((sum, lesson) => sum + lesson.completedPrompts, 0);
    const totalExercises = course.lessons.reduce((sum, lesson) => sum + lesson.totalPrompts, 0);

    return (
        <Box sx={{ maxWidth: 1180, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
            <Button onClick={onBack} startIcon={<ArrowBackRoundedIcon />} sx={{ minHeight: 44, mb: 1.5, px: 0, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: BLUE, textTransform: 'none' }}>My courses</Button>

            <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 }, mb: 3, overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', bgcolor: `${course.accentColor}0B`, right: -50, top: -80 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2.5} sx={{ position: 'relative' }}>
                    <Box sx={{ width: 62, height: 62, flexShrink: 0, borderRadius: '18px', display: 'grid', placeItems: 'center', bgcolor: `${course.accentColor}1A` }}><CourseIcon sx={{ color: course.accentColor, fontSize: 32 }} /></Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.09em', color: SLATE }}>MY LESSONS</Typography>
                        <Typography component="h1" sx={{ mt: 0.5, fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 22, md: 27 }, color: NAVY }}>{course.name}</Typography>
                        <Typography sx={{ mt: 0.3, fontFamily: INTER, fontSize: 13, color: SLATE }}>{course.subtitle}</Typography>
                    </Box>
                    <ProgressRing percent={percent} size={86} thickness={5} showLabel />
                </Stack>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.75fr) minmax(270px, .75fr)' }, gap: 2.5, alignItems: 'start' }}>
                <Box>
                    <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Lessons</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>{completed} of {course.lessons.length} complete</Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                        {course.lessons.map(lesson => <LessonCard key={lesson.id} lesson={lesson} isCurrent={currentLessonId === lesson.id} onOpen={fromBeginning => onOpenLesson(lesson, fromBeginning)} />)}
                    </Stack>
                </Box>

                <Stack spacing={2}>
                    <Box sx={{ ...cardSx, p: 2.5 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15.5, color: NAVY }}>Course overview</Typography>
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                            <Box sx={{ p: 1.75, borderRadius: '14px', bgcolor: SURFACE_SOFT }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY }}>{completed}/{course.lessons.length}</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}>lessons completed</Typography>
                            </Box>
                            <Box sx={{ p: 1.75, borderRadius: '14px', bgcolor: SURFACE_SOFT }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY }}>{completedExercises}/{totalExercises}</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}>exercises completed</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {nextLesson && (
                        <Box sx={{ ...cardSx, p: 2.5 }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: BLUE }}>UP NEXT</Typography>
                            <Typography sx={{ mt: 0.65, fontFamily: POPPINS, fontWeight: 600, fontSize: 14, lineHeight: 1.4, color: NAVY }}>{nextLesson.name}</Typography>
                            <Typography sx={{ mt: 0.4, fontFamily: INTER, fontSize: 11.5, color: SLATE }}>{nextLesson.completedPrompts > 0 ? `${lessonPercent(nextLesson)}% complete` : `${nextLesson.totalPrompts} exercises`}</Typography>
                            <Typography sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${RING_TRACK}`, fontFamily: INTER, fontSize: 11.5, lineHeight: 1.5, color: SLATE }}>Use the highlighted lesson in the list to continue.</Typography>
                        </Box>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}
